import { type ComponentPropsWithRef, forwardRef, type ReactNode } from "react";
import { Icon, type IconComponent } from "../Icon";
import { Spinner } from "../Spinner";
import styles from "./Button.module.css";

/**
 * Button — text-label action primitive.
 *
 * Composes Medium-weight typography + optional leading/trailing `Icon` +
 * optional `Spinner` (replaces the leading slot during `loading`).
 *
 * Variants map to the Figma button type enum:
 *   - `primary`   → cyan→green vertical gradient (the brand CTA)
 *   - `secondary` → --button-secondary-bg-default (neutral fill)
 *   - `outline`   → --button-outline-stroke border, hover paints --button-outline-bg-hover
 *   - `ghost`     → transparent, hover paints --button-outline-bg-hover
 *   - `negative`  → --button-negative-bg (destructive)
 *
 * Sizes mirror the Figma size enum:
 *   - `xs` — tiny, rounded-12, 12px text, 14px icon (used in tight toolbars)
 *   - `s`  — rounded-8, 14px text, 16px icon, ~28px tall
 *   - `m`  — rounded-8, 14px text, 16px icon, fixed 40px tall (default)
 *
 * `negative-ghost` is the demoted destructive: red label, no fill, no border.
 * For a destructive verb that must stay reachable but must not dominate the
 * page it sits on (e.g. Disconnect in the settings footer). Use `negative`
 * when the destructive action IS the point of the surface — including the
 * confirm step of a two-step flow, where the commit button should be loud.
 *
 * The "Link Button" Figma variant is intentionally NOT a Button variant here —
 * it's structurally a link (no background, no border, different text color)
 * and ships as the dedicated `<Link>` primitive instead.
 *
 * Loading state: renders a `<Spinner tone="inherit">` in the leading icon slot,
 * keeps the label visible so the button width doesn't jump, and disables the
 * button so it can't be clicked while the async action is in flight.
 *
 * Accessibility:
 *   - `disabled` and `loading` both disable the native button; loading also
 *     sets `aria-busy="true"`
 *   - the label is the visible text (children); no aria-label override
 *   - icons are decorative by default (no per-icon labels) since the button's
 *     text already describes the action
 *   - the loading spinner is decorative by default: `aria-busy="true"` plus
 *     the still-visible label already convey the loading state, so a separate
 *     `role="status"` "Loading" announcement would be redundant. Pass
 *     `loadingLabel` to opt into an explicit spinner announcement instead.
 */

type Variant = "primary" | "secondary" | "outline" | "ghost" | "negative" | "negative-ghost";
type Size = "xs" | "s" | "m";

export type ButtonProps = {
  variant?: Variant;
  size?: Size;
  /** Optional icon rendered before the label. Hidden when `loading` (spinner takes its slot). */
  leadingIcon?: IconComponent;
  /**
   * Optional icon rendered after the label.
   * Hidden during `loading` so the button shows only [Spinner][label]
   * rather than an inconsistent [Spinner][label][TrailingIcon] state.
   */
  trailingIcon?: IconComponent;
  /** Shows a spinner in the leading slot and disables the button. */
  loading?: boolean;
  /**
   * Optional accessible label for the loading spinner. When omitted the
   * spinner is decorative (`aria-hidden`) — `aria-busy` + the visible
   * label already announce the loading state. Provide this only when an
   * explicit spinner announcement is wanted.
   */
  loadingLabel?: string;
  /** Makes the button fill its parent horizontally. */
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
} & Omit<ComponentPropsWithRef<"button">, "className" | "children" | "aria-busy">;

// Icon + Spinner sizing inside the button.
// xs button → 14px glyph; s/m → 16px glyph.
const GLYPH_SIZE: Record<Size, "xs" | "sm"> = {
  xs: "xs",
  s: "sm",
  m: "sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "m",
    leadingIcon,
    trailingIcon,
    loading,
    loadingLabel,
    fullWidth,
    disabled,
    type,
    className,
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const classes = [
    styles.button,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    fullWidth ? styles.fullWidth : null,
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const glyph = GLYPH_SIZE[size];

  // `type` defaults to "button" so a Button inside a <form> doesn't
  // accidentally trigger form submission. Consumers can pass type="submit"
  // explicitly for submit buttons.
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        loadingLabel ? (
          <Spinner size={glyph} tone="inherit" label={loadingLabel} />
        ) : (
          <Spinner size={glyph} tone="inherit" decorative />
        )
      ) : leadingIcon ? (
        <Icon as={leadingIcon} size={glyph} tone="inherit" />
      ) : null}
      <span className={styles.label}>{children}</span>
      {trailingIcon && !loading ? <Icon as={trailingIcon} size={glyph} tone="inherit" /> : null}
    </button>
  );
});
